import React, { useCallback, useMemo, useState } from 'react';
import type { Lot, Portfolio } from 'venn-api';
import { AlertNote, Button, ErrorNote } from 'venn-ui-kit';
import {
  AddInvestmentCommonHeader,
  AddInvestmentCommonModal,
  TAX_MODAL_WIDTH,
} from '../../modals/add-investment-common-modal';
import { ModalFooter } from '../../modal';
import type { LotInput } from '../allocation-search/tax-info';
import { convertLotsToApi, createDefaultLot, lotsToInputs, TaxInfoStep } from '../allocation-search/tax-info';
import { getLotIncompleteFields } from '../allocation-search/tax-info/TaxLotInputRow';

type TaxInfoModalProps = {
  node: Portfolio;
  onClose: () => void;
  onSave: (lots: Lot[]) => void;
};

export const TaxInfoModal = ({ node, onClose, onSave }: TaxInfoModalProps) => {
  const existingLots = node.lots ?? [];
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false);
  const [lots, setLots] = useState<LotInput[]>(
    existingLots.length > 0 ? lotsToInputs(existingLots) : [createDefaultLot()],
  );

  const handleSave = useCallback(() => {
    onSave(convertLotsToApi(lots));
    onClose();
  }, [lots, onSave, onClose]);

  const hasAtLeastOneLotFilled = lots.some((lot) => lot.marketValue !== 0 && lot.costBasis !== 0 && lot.quantity !== 0);

  const hasIncompleteLots = useMemo(
    () =>
      lots.some((lot) => {
        const incompleteFields = getLotIncompleteFields(lot);
        return (
          incompleteFields !== undefined &&
          (incompleteFields.marketValue || incompleteFields.costBasis || incompleteFields.quantity)
        );
      }),
    [lots],
  );

  const investmentName = node.fund?.name ?? node.name ?? '';
  const title = (
    <>
      <strong>Edit tax information: </strong>
      {investmentName}
    </>
  );

  return (
    <AddInvestmentCommonModal onClose={onClose} size={TAX_MODAL_WIDTH}>
      <AddInvestmentCommonHeader title={title} onClose={onClose} />
      <TaxInfoStep lots={lots} onLotsChange={setLots} setSaveDisabled={setIsSaveDisabled} />
      {hasIncompleteLots && (
        <ErrorNote size="small" className="justify-start">
          Some tax information is missing. Full tax analysis cannot be run until all information is present.
        </ErrorNote>
      )}
      <AlertNote size="small" className="justify-start">
        Lots added to this portfolio apply wherever this portfolio is used.
      </AlertNote>
      <ModalFooter
        className="mt-0 py-3 px-4 border-t-venn-grey h-16"
        inverted
        simplifiedButtonStyles
        leftChildren={
          <Button refreshedStyling noMargin onClick={onClose}>
            Cancel
          </Button>
        }
        primaryLabel={<span>Save</span>}
        primaryDisabled={!hasAtLeastOneLotFilled || hasIncompleteLots || isSaveDisabled}
        onPrimaryClick={handleSave}
      />
    </AddInvestmentCommonModal>
  );
};
